const WISHLIST_STORAGE_KEY = 'Jagraa_wishlist';

class JagraaWishlist {
  static getItems() {
    try {
      const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      console.warn('Wishlist read failed', error);
      return [];
    }
  }

  static saveItems(items) {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
    publish(PUB_SUB_EVENTS.wishlistUpdate, { items, count: items.length });
  }

  static has(productId) {
    return this.getItems().some((item) => String(item.id) === String(productId));
  }

  static add(item) {
    const items = this.getItems();
    if (items.some((entry) => String(entry.id) === String(item.id))) return false;
    items.push(item);
    this.saveItems(items);
    return true;
  }

  static remove(productId) {
    const items = this.getItems().filter((entry) => String(entry.id) !== String(productId));
    this.saveItems(items);
  }

  static toggle(item) {
    if (this.has(item.id)) {
      this.remove(item.id);
      return false;
    }
    this.add(item);
    return true;
  }

  static getCount() {
    return this.getItems().length;
  }

  static escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  static async addVariantToCart(variantId) {
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    if (cart?.getSectionsToRender) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id).join(',')
      );
      formData.append('sections_url', window.location.pathname);
    }

    config.body = formData;
    const response = await fetch(window.routes.cart_add_url, config);
    const data = await response.json();

    if (data.status) {
      throw new Error(data.description || data.message || 'Add to cart failed');
    }

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'wishlist',
      productVariantId: variantId,
      cartData: data,
    });

    if (cart?.renderContents && data.sections) {
      cart.renderContents(data);
    }

    return data;
  }
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-wishlist-toggle]');
    if (!this.button) return;

    this.isCardButton = this.classList.contains('wishlist-button--card');
    this.onClick = this.handleClick.bind(this);

    if (!this.isCardButton) {
      this.button.addEventListener('click', this.onClick);
    }

    this.unsubscribeWishlist = subscribe(PUB_SUB_EVENTS.wishlistUpdate, () => this.updateState());
    this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (!event?.data?.variant) return;
      if (this.dataset.sectionId && event.data.sectionId !== this.dataset.sectionId) return;
      this.dataset.variantId = event.data.variant.id;
      this.dataset.productAvailable = event.data.variant.available;
    });

    this.updateState();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    this.unsubscribeWishlist?.();
    this.unsubscribeVariant?.();
  }

  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const item = this.getItemFromDataset();
    const isActive = JagraaWishlist.toggle(item);
    this.updateState(isActive);
  }

  getItemFromDataset() {
    return {
      id: this.dataset.productId,
      variantId: this.dataset.variantId,
      handle: this.dataset.productHandle,
      title: this.dataset.productTitle,
      url: this.dataset.productUrl,
      image: this.dataset.productImage,
      price: this.dataset.productPrice,
      compareAtPrice: this.dataset.productCompareAtPrice || '',
      available: this.dataset.productAvailable !== 'false',
    };
  }

  updateState(forcedActive) {
    const isActive = typeof forcedActive === 'boolean' ? forcedActive : JagraaWishlist.has(this.dataset.productId);
    this.classList.toggle('is-active', isActive);
    this.button?.setAttribute('aria-pressed', isActive ? 'true' : 'false');

    const title = this.dataset.productTitle || '';
    const strings = window.wishlistStrings || {};
    const label = isActive
      ? strings.removeLabel || `Remove ${title} from wishlist`
      : strings.addLabel || `Add ${title} to wishlist`;
    this.button?.setAttribute('aria-label', label);
  }
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.countEl = this.querySelector('[data-wishlist-count]');
    this.bubble = this.querySelector('.cart-count-bubble') || this.closest('.header__icon--wishlist')?.querySelector('.cart-count-bubble');
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, () => this.render());
    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  render() {
    const count = JagraaWishlist.getCount();
    if (this.countEl) this.countEl.textContent = count;
    if (this.bubble) this.bubble.hidden = count < 1;
  }
}

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, () => this.render());
    this.grid?.addEventListener('click', this.onGridClick.bind(this));
    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    this.grid?.removeEventListener('click', this.onGridClick.bind(this));
  }

  onGridClick(event) {
    const removeBtn = event.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      event.preventDefault();
      JagraaWishlist.remove(removeBtn.dataset.wishlistRemove);
      return;
    }

    const addBtn = event.target.closest('[data-wishlist-add-to-cart]');
    if (addBtn) {
      event.preventDefault();
      this.addToCart(addBtn);
    }
  }

  render() {
    const items = JagraaWishlist.getItems();
    const strings = window.wishlistPageStrings || window.wishlistStrings || {};

    if (!this.grid || !this.empty) return;

    if (!items.length) {
      this.grid.innerHTML = '';
      this.grid.hidden = true;
      this.empty.hidden = false;
      return;
    }

    this.empty.hidden = true;
    this.grid.hidden = false;
    this.grid.innerHTML = items
      .map((item) => this.renderItem(item, strings))
      .join('');
  }

  renderItem(item, strings) {
    const compareHtml =
      item.compareAtPrice && item.compareAtPrice !== item.price
        ? `<span class="wishlist-page__compare">${item.compareAtPrice}</span>`
        : '';

    const addLabel = item.available ? strings.addToCart || 'Add to cart' : strings.soldOut || 'Sold out';
    const removeLabel = strings.remove || 'Remove';
    const viewLabel = strings.viewProduct || 'View product';

    return `
      <li class="grid__item">
        <article class="wishlist-page__item" data-wishlist-item="${item.id}">
          <div class="wishlist-page__card">
            <a href="${item.url}" class="wishlist-page__media" tabindex="-1" aria-hidden="true">
              <img src="${item.image}" alt="${JagraaWishlist.escapeHtml(item.title)}" loading="lazy" width="400" height="500">
            </a>
            <div class="wishlist-page__body">
              <h2 class="wishlist-page__title-wrap">
                <a href="${item.url}" class="wishlist-page__title">${JagraaWishlist.escapeHtml(item.title)}</a>
              </h2>
              <div class="wishlist-page__price">
                <span class="wishlist-page__price-current">${item.price}</span>
                ${compareHtml}
              </div>
              <div class="wishlist-page__actions">
                <button
                  type="button"
                  class="button button--primary wishlist-page__add"
                  data-wishlist-add-to-cart
                  data-variant-id="${item.variantId}"
                  ${item.available ? '' : 'disabled'}
                >
                  ${addLabel}
                </button>
                <button
                  type="button"
                  class="button wishlist-page__remove"
                  data-wishlist-remove="${item.id}"
                >
                  ${removeLabel}
                </button>
              </div>
              <a href="${item.url}" class="wishlist-page__view link underlined-link">${viewLabel}</a>
            </div>
          </div>
        </article>
      </li>
    `;
  }

  async addToCart(button) {
    if (button.disabled || button.classList.contains('loading')) return;

    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');

    try {
      await JagraaWishlist.addVariantToCart(button.dataset.variantId);
    } catch (error) {
      console.error(error);
    } finally {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
    }
  }
}

class CartDrawerWishlist extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.onListClick = this.onClick.bind(this);
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, () => this.render());
    this.list?.addEventListener('click', this.onListClick);
    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    this.list?.removeEventListener('click', this.onListClick);
  }

  onClick(event) {
    const removeBtn = event.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      event.preventDefault();
      JagraaWishlist.remove(removeBtn.dataset.wishlistRemove);
      return;
    }

    const addBtn = event.target.closest('[data-wishlist-add-to-cart]');
    if (addBtn) {
      event.preventDefault();
      this.addToCart(addBtn);
    }
  }

  render() {
    const items = JagraaWishlist.getItems();
    const strings = window.wishlistStrings || {};
    const emptyText = this.dataset.emptyText || strings.emptyDrawer || 'No saved items yet';

    if (!this.list || !this.empty) return;

    this.classList.toggle('is-empty', items.length === 0);

    if (!items.length) {
      this.list.innerHTML = '';
      this.list.hidden = true;
      this.empty.hidden = false;
      this.empty.textContent = emptyText;
      return;
    }

    this.empty.hidden = true;
    this.list.hidden = false;
    this.list.innerHTML = items.map((item) => this.renderItem(item, strings)).join('');
  }

  renderItem(item, strings) {
    const title = JagraaWishlist.escapeHtml(item.title);
    const addLabel = item.available ? strings.addToCart || 'Add to cart' : strings.soldOut || 'Sold out';
    const removeLabel = strings.remove || 'Remove';
    const compareHtml =
      item.compareAtPrice && item.compareAtPrice !== item.price
        ? `<s class="cart-drawer-wishlist__compare">${item.compareAtPrice}</s>`
        : '';
    const image = item.image
      ? `<img src="${item.image}" alt="${title}" width="72" height="90" loading="lazy">`
      : '';

    return `
      <li class="cart-drawer-wishlist__item" data-wishlist-item="${item.id}">
        <a href="${item.url}" class="cart-drawer-wishlist__media" tabindex="-1" aria-hidden="true">
          ${image}
        </a>
        <div class="cart-drawer-wishlist__details">
          <a href="${item.url}" class="cart-drawer-wishlist__title">${title}</a>
          <div class="cart-drawer-wishlist__price">
            <span>${item.price}</span>
            ${compareHtml}
          </div>
          <div class="cart-drawer-wishlist__actions">
            <button
              type="button"
              class="button button--primary cart-drawer-wishlist__add"
              data-wishlist-add-to-cart
              data-variant-id="${item.variantId}"
              ${item.available ? '' : 'disabled'}
            >
              ${addLabel}
            </button>
            <button
              type="button"
              class="cart-drawer-wishlist__remove"
              data-wishlist-remove="${item.id}"
              aria-label="${removeLabel}"
            >
              ${removeLabel}
            </button>
          </div>
        </div>
      </li>
    `;
  }

  async addToCart(button) {
    if (button.disabled || button.classList.contains('loading')) return;

    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');

    try {
      await JagraaWishlist.addVariantToCart(button.dataset.variantId);
    } catch (error) {
      console.error(error);
    } finally {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}

if (!customElements.get('cart-drawer-wishlist')) {
  customElements.define('cart-drawer-wishlist', CartDrawerWishlist);
}

function initWishlistPageFallback() {
  if (!window.location.pathname.includes('/pages/wishlist')) return;

  const mainContent = document.querySelector('main#MainContent') || document.querySelector('#MainContent') || document.querySelector('.main-content');
  const template404 = document.querySelector('.template-404');

  if (template404 && mainContent) {
    mainContent.innerHTML = `
      <div class="color-scheme-1 gradient">
        <div class="page-width section-template--wishlist-padding" style="padding-top: 36px; padding-bottom: 48px;">
          <h1 class="main-page-title page-title h0" style="text-align: center;">Wishlist</h1>
          <wishlist-page class="wishlist-page">
            <p class="wishlist-page__subtitle" style="text-align: center; margin-bottom: 2rem;">
              Products you love, saved for later.
            </p>

            <div class="wishlist-page__empty" data-wishlist-empty style="text-align: center;" hidden>
              <h2 class="wishlist-page__empty-title h2">Your wishlist is empty</h2>
              <p class="wishlist-page__empty-text">Save your favourite pieces with the heart icon and find them here anytime.</p>
              <a href="/collections/all" class="button button--primary" style="margin-top: 1.5rem; display: inline-block;">
                Continue shopping
              </a>
            </div>

            <ul
              class="wishlist-page__grid grid product-grid grid--4-col-desktop grid--2-col-tablet-down"
              data-wishlist-grid
              role="list"
              hidden
            ></ul>
          </wishlist-page>
        </div>
      </div>
    `;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initWishlistPageFallback();
  publish(PUB_SUB_EVENTS.wishlistUpdate, {
    items: JagraaWishlist.getItems(),
    count: JagraaWishlist.getCount(),
  });
});

function getWishlistItemFromHost(host) {
  return {
    id: host.dataset.productId,
    variantId: host.dataset.variantId,
    handle: host.dataset.productHandle,
    title: host.dataset.productTitle,
    url: host.dataset.productUrl,
    image: host.dataset.productImage,
    price: host.dataset.productPrice,
    compareAtPrice: host.dataset.productCompareAtPrice || '',
    available: host.dataset.productAvailable !== 'false',
  };
}

function handleCardWishlistClick(event) {
  const toggle = event.target.closest('[data-wishlist-toggle]');
  if (!toggle) return;

  const host = toggle.closest('wishlist-button.wishlist-button--card');
  if (!host) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  JagraaWishlist.toggle(getWishlistItemFromHost(host));
}

function handleHeaderWishlistClick(event) {
  const wishlistIcon = event.target.closest('#wishlist-icon-bubble, .header__icon--wishlist');
  if (!wishlistIcon) return;
  const href = wishlistIcon.getAttribute('href');
  if (href && href !== '#') {
    window.location.href = href;
  }
}

document.addEventListener('click', handleCardWishlistClick, true);
document.addEventListener('click', handleHeaderWishlistClick);
